<template>
  <nav
    v-if="!isAppview"
    class="breadcrumbs"
    :class="{
      'breadcrumbs--no-underline': noUnderline,
    }"
    :aria-label="$t('accessibility.breadcrumbsNavigation')"
  >
    <ul class="b-items">
      <li
        id="b-item-home"
        class="b-item b-item--home"
      >
        <fn-link
          to="/"
          no-arrow
          :color="color"
          :aria-label="$t('accessibility.general.goToHomepage')"
        >
          <fn-icon
            icon="house"
            :color="color"
            role="presentation"
          />
        </fn-link>
        <fn-icon
          icon="arrow-right"
          size="xxs"
          :color="color"
          role="presentation"
        />
      </li>
      <li
        v-for="(node, index) in nodes"
        :id="'b-item-' + index"
        :key="node.id"
        class="b-item"
        :class="{
          'b-item--last': lastItem(index),
          'b-item--white': white,
        }"
        :aria-current="lastItem(index) ? 'page' : undefined"
      >
        <fn-link
          v-if="!lastItem(index)"
          :to="node.truncateQuery ? node.truncateQuery.url : node.url"
          no-arrow
        >
          <span v-html="node.facetValueName ? node.facetValueName : node.title" />
        </fn-link>

        <fn-icon
          v-if="!lastItem(index)"
          icon="arrow-right"
          role="presentation"
        />

        <span
          v-if="lastItem(index)"
          v-html="node.facetValueName ? node.facetValueName : node.title"
        />
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    nodes: Record<string, any>[];
    noUnderline?: boolean;
    white?: boolean;
  }>(),
  {
    nodes: () => [],
    noUnderline: false,
    white: false,
  },
);

const { isAppview } = useMainStore();
const color = computed(() => (props.white ? 'white' : ''));

const lastItem = (index: number) => index + 1 === props.nodes.length;
</script>

<style lang="scss" scoped>
.breadcrumbs {
  font-size: map-get($font-sizes, 'm');

  @include bp(m) {
    border-bottom: 1px solid palette(grey, light);

    &.breadcrumbs--no-underline {
      border-bottom: none;
    }
  }

  .b-items {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  .b-item {
    display: flex;
    align-items: center;

    .link {
      padding: 0 5px;
      @include bp(m) {
        padding: 6px 12px;
      }
    }

    .icon {
      font-size: 8px;

      @include bp(m) {
        margin: 0 2px;
        font-size: map-get($font-sizes, 's');
      }
    }

    &.b-item--home {
      .icon {
        margin-left: 0;
        font-size: 8px;

        @include bp(m) {
          font-size: map-get($font-sizes, 's');
        }
      }

      .link {
        text-decoration: none;

        &:hover,
        &:focus,
        &:active {
          text-decoration: none;
        }

        .icon {
          font-size: map-get($font-sizes, 'm');
        }

        @include bp(m) {
          padding-left: 0;
        }
      }
    }

    &.b-item--last {
      padding: 0 5px;
      font-weight: bold;

      @include bp(m) {
        padding: 6px 12px;
      }
    }

    &.b-item--white {
      color: $color-general-text-inverse-default;
    }
  }
}
</style>
